import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { Card, Icon, SearchBar } from 'components';
import { CardProps } from 'components/Card';
import { ClearButton, SearchIconContainer } from './styled';

type LayoutCardMobileProps = {
  search?: {
    searchValue: string;
    onSearchValueChange: (value: string) => void;
  };
  titleLink?:
    | {
        title: React.ReactNode;
        linkPath: string;
      }
    | false
    | null;
  titleExtra?: React.ReactNode;
  titleExtraPosition?: 'left' | 'right';
  hideViewAll?: boolean;
};

const LayoutCardMobile: React.FC<
  Omit<CardProps, 'type'> & LayoutCardMobileProps
> = ({
  search,
  titleLink,
  tabList,
  children,
  titleExtra,
  titleExtraPosition = 'right',
  hideViewAll,
  ...props
}) => {
  const [isSearchBarOpen, setIsSearchBarOpen] = useState(false);

  const searchBarRef = useRef(null);
  const [wrapper, setWrapper] = useState<HTMLDivElement>(null);

  useEffect(() => {
    if (isSearchBarOpen && searchBarRef.current) {
      searchBarRef.current.focus();
    }
  }, [isSearchBarOpen]);

  const getTopOffset = () => {
    if (!wrapper) return 0;
    const { y } = wrapper.getBoundingClientRect();
    return y + 1; // Fixes rounding errors
  };

  const tabListWithViewAllLink =
    titleLink && !hideViewAll
      ? [
          ...tabList,
          {
            key: 'titleLink',
            tab: <Link to={titleLink.linkPath}>View All</Link>,
          },
        ]
      : [...tabList];

  const mobileSearchProps =
    search &&
    (isSearchBarOpen
      ? {
          title: (
            <StyledExtraWrapper $searchOpen>
              {titleExtraPosition === 'left' && titleExtra}
              <SearchBar
                ref={searchBarRef}
                value={search.searchValue}
                onChange={search.onSearchValueChange}
                placeholder="Search Here"
                suffix={
                  <ClearButton
                    onClick={() => {
                      setIsSearchBarOpen(false);
                      search.onSearchValueChange('');
                    }}
                  >
                    <Icon type="x" />
                  </ClearButton>
                }
              />
              {titleExtraPosition === 'right' && titleExtra}
            </StyledExtraWrapper>
          ),
        }
      : {
          tabBarExtraContent: (
            <StyledExtraWrapper>
              {titleExtraPosition === 'left' && titleExtra}
              <SearchIconContainer onClick={() => setIsSearchBarOpen(true)}>
                <Icon type="search" offset={[5, 2]} color="gray10" />
              </SearchIconContainer>
              {titleExtraPosition === 'right' && titleExtra}
            </StyledExtraWrapper>
          ),
        });

  return (
    <StyledCardMobile
      {...props}
      title={null}
      tabList={isSearchBarOpen ? null : tabListWithViewAllLink}
      $topOffset={getTopOffset()}
      {...mobileSearchProps}
    >
      <div ref={setWrapper}>{children}</div>
    </StyledCardMobile>
  );
};

export default LayoutCardMobile;

const StyledExtraWrapper = styled.div<{ $searchOpen?: boolean }>`
  display: grid;
  grid-template-columns: 1fr 40px;
  align-items: center;
  justify-content: space-between;
  ${({ $searchOpen }) => $searchOpen && 'padding: 0 25px'};
`;

const StyledCardMobile = styled(Card)<{ $topOffset: number }>`
  width: 100%;
  border-top: none;

  .ant-card-head {
    padding: 0;
    border: none;
  }

  .ant-card-body {
    height: ${({ $topOffset }) => `calc(100vh - ${$topOffset}px)`};
    padding: 0;
    overflow-y: auto;

    > div {
      height: 100%;
    }
  }
`;
